import { createBrowserRouter, RouterProvider, useSearchParams } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import Layout from './components/Layout';
import PrivateRoute from './components/PrivateRoute';
import EditorRoute from './components/EditorRoute';
import StudentRoute from './components/StudentRoute';
import ThemesRoute from './components/ThemesRoute';
import AdminRoute from './components/AdminRoute';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import TaskListPage from './pages/TaskListPage';
import TaskDetailPage from './pages/TaskDetailPage';
import TaskEditorPage from './pages/TaskEditorPage';
import BulkImportPage from './pages/BulkImportPage';
import CollaborativePage from './pages/CollaborativePage';
import WorkPage from './pages/WorkPage';
import MyTasksPage from './pages/MyTasksPage';
import MyAttemptsPage from './pages/MyAttemptsPage';
import MyLessonsPage from './pages/MyLessonsPage';
import QuizReviewPage from './pages/QuizReviewPage';
import MySchedulePage from './pages/MySchedulePage';
import MyMessagesPage from './pages/MyMessagesPage';
import TeacherMessagesPage from './pages/TeacherMessagesPage';
import PublicSchedulePage from './pages/PublicSchedulePage';
import TeacherDashboardPage from './pages/TeacherDashboardPage';
import TeacherSchedulePage from './pages/TeacherSchedulePage';
import TeacherStudentWorkPage from './pages/TeacherStudentWorkPage';
import TeacherStudentQuestionnairePage from './pages/TeacherStudentQuestionnairePage';
import TeacherStudentActivityPage from './pages/TeacherStudentActivityPage';
import TeacherListCreatePage from './pages/TeacherListCreatePage';
import DashboardOrRedirect from './components/DashboardOrRedirect';
import ProfilePage from './pages/ProfilePage';
import QuestionnairePage from './pages/QuestionnairePage';
import InvitePage from './pages/InvitePage';
import MyTeachersPage from './pages/MyTeachersPage';
import StudentTopicProgressPage from './pages/StudentTopicProgressPage';
import TaskCatalogPage from './pages/TaskCatalogPage';
import TaskCatalogPrintPage from './pages/TaskCatalogPrintPage';
import EditorSettingsPage from './pages/EditorSettingsPage';
import TeacherTagsPage from './pages/TeacherTagsPage';
import TeacherEgeMapPage from './pages/TeacherEgeMapPage';
import TeacherNewUsersPage from './pages/TeacherNewUsersPage';
import TeacherNewUserActivityPage from './pages/TeacherNewUserActivityPage';
import TopicProgramPage from './pages/TopicProgramPage';
import OgePythonPathPage from './pages/OgePythonPathPage';
import GameCreationPathPage from './pages/GameCreationPathPage';
import AdminTopicProgramsPage from './pages/AdminTopicProgramsPage';
import TaskBankCleanupPage from './pages/TaskBankCleanupPage';
import TeacherToolsPage from './pages/TeacherToolsPage';
import TeacherTaskInboxPage from './pages/TeacherTaskInboxPage';
import TaskBankFamiliesPage from './pages/TaskBankFamiliesPage';
import KonspektyListPage from './pages/KonspektyListPage';
import KonspektDetailPage from './pages/KonspektDetailPage';
import KonspektFormPage from './pages/KonspektFormPage';
import PracticeQuizzesPage from './pages/PracticeQuizzesPage';
import TrainersPage from './pages/TrainersPage';
import TrainerSessionPage from './pages/TrainerSessionPage';
import BillingPage from './pages/BillingPage';
import BillingReturnPage from './pages/BillingReturnPage';
import TeacherBillingPage from './pages/TeacherBillingPage';
import TeacherStudentBillingPage from './pages/TeacherStudentBillingPage';
import ParentHomePage from './pages/ParentHomePage';
import ParentChildPage, { ParentChildTabRedirect } from './pages/ParentChildPage';
import ParentInvitePage from './pages/ParentInvitePage';
import ChildInvitePage from './pages/ChildInvitePage';
import ParentQuestionnairePage from './pages/ParentQuestionnairePage';
import PrivacyPage from './pages/PrivacyPage';
import ConsentPage from './pages/ConsentPage';
import OfertaPage from './pages/OfertaPage';
import NotFoundPage from './pages/NotFoundPage';
import './App.css';

function GuestOrPrivateWork() {
  const [searchParams] = useSearchParams();
  if (searchParams.get('guest') === '1') {
    return <WorkPage />;
  }
  return (
    <PrivateRoute>
      <WorkPage />
    </PrivateRoute>
  );
}

/** Data Router: нужен для useBlocker на странице работы с задачей (WorkPage). */
const router = createBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    children: [
      { index: true, element: <PrivateRoute><DashboardOrRedirect /></PrivateRoute> },
      { path: 'login', element: <LoginPage /> },
      { path: 'register', element: <RegisterPage /> },
      { path: 'forgot-password', element: <ForgotPasswordPage /> },
      { path: 'reset-password/:uid/:token', element: <ResetPasswordPage /> },
      { path: 'privacy', element: <PrivacyPage /> },
      { path: 'consent', element: <ConsentPage /> },
      { path: 'oferta', element: <OfertaPage /> },
      { path: 'schedule/public/:token', element: <PublicSchedulePage /> },
      { path: 'profile', element: <PrivateRoute><ProfilePage /></PrivateRoute> },
      { path: 'questionnaire', element: <PrivateRoute><QuestionnairePage /></PrivateRoute> },
      { path: 'billing/return', element: <PrivateRoute><BillingReturnPage /></PrivateRoute> },
      { path: 'billing', element: <PrivateRoute><BillingPage /></PrivateRoute> },
      { path: 'parent/children/:studentId/topic-progress', element: <PrivateRoute><ParentChildTabRedirect tab="topics" /></PrivateRoute> },
      { path: 'parent/children/:studentId/activity', element: <PrivateRoute><ParentChildTabRedirect tab="activity" /></PrivateRoute> },
      { path: 'parent/children/:studentId/questionnaire', element: <PrivateRoute><ParentQuestionnairePage /></PrivateRoute> },
      { path: 'parent/children/:studentId', element: <PrivateRoute><ParentChildPage /></PrivateRoute> },
      { path: 'parent', element: <PrivateRoute><ParentHomePage /></PrivateRoute> },
      { path: 'settings/editor', element: <PrivateRoute><EditorSettingsPage /></PrivateRoute> },
      { path: 'invite/:token', element: <InvitePage /> },
      { path: 'parent-invite/:token', element: <ParentInvitePage /> },
      { path: 'child-invite/:token', element: <ChildInvitePage /> },
      { path: 'tasks', element: <PrivateRoute><StudentRoute><TaskListPage /></StudentRoute></PrivateRoute> },
      { path: 'oge-python', element: <ThemesRoute><OgePythonPathPage /></ThemesRoute> },
      { path: 'themes/osnovy_python_oge', element: <ThemesRoute><OgePythonPathPage /></ThemesRoute> },
      { path: 'sozdanie-igr', element: <ThemesRoute><GameCreationPathPage /></ThemesRoute> },
      { path: 'themes/sozdanie_igr', element: <ThemesRoute><GameCreationPathPage /></ThemesRoute> },
      { path: 'themes/:slug', element: <ThemesRoute><TopicProgramPage /></ThemesRoute> },
      { path: 'admin/topic-programs', element: <AdminRoute><AdminTopicProgramsPage /></AdminRoute> },
      { path: 'admin/task-cleanup', element: <AdminRoute><TaskBankCleanupPage /></AdminRoute> },
      { path: 'catalog/print/:tagSlug', element: <PrivateRoute><TaskCatalogPrintPage /></PrivateRoute> },
      { path: 'catalog', element: <PrivateRoute><TaskCatalogPage /></PrivateRoute> },
      { path: 'practice-quizzes', element: <PracticeQuizzesPage /> },
      { path: 'trainers', element: <TrainersPage /> },
      { path: 'trainers/sessions/:sessionId', element: <TrainerSessionPage /> },
      { path: 'tasks/:slug', element: <PrivateRoute><TaskDetailPage /></PrivateRoute> },
      { path: 'work/:taskSlug', element: <GuestOrPrivateWork /> },
      { path: 'sessions/:lessonSessionId/tasks/:taskSlug', element: <PrivateRoute><WorkPage /></PrivateRoute> },
      { path: 'my-tasks', element: <PrivateRoute><MyTasksPage /></PrivateRoute> },
      { path: 'my-attempts', element: <PrivateRoute><MyAttemptsPage /></PrivateRoute> },
      { path: 'my-lessons', element: <PrivateRoute><StudentRoute><MyLessonsPage /></StudentRoute></PrivateRoute> },
      { path: 'quiz-review/:listId', element: <PrivateRoute><QuizReviewPage /></PrivateRoute> },
      { path: 'my-schedule', element: <PrivateRoute><MySchedulePage /></PrivateRoute> },
      { path: 'my-messages', element: <PrivateRoute><MyMessagesPage /></PrivateRoute> },
      { path: 'konspekty/new', element: <PrivateRoute><KonspektFormPage /></PrivateRoute> },
      { path: 'konspekty/:slug/edit', element: <PrivateRoute><KonspektFormPage /></PrivateRoute> },
      { path: 'konspekty/:slug', element: <PrivateRoute><KonspektDetailPage /></PrivateRoute> },
      { path: 'konspekty', element: <PrivateRoute><KonspektyListPage /></PrivateRoute> },
      { path: 'my-teachers', element: <PrivateRoute><MyTeachersPage /></PrivateRoute> },
      { path: 'topic-progress', element: <PrivateRoute><StudentTopicProgressPage /></PrivateRoute> },
      { path: 'dashboard', element: <PrivateRoute><DashboardOrRedirect /></PrivateRoute> },
      { path: 'teacher/schedule', element: <EditorRoute><TeacherSchedulePage /></EditorRoute> },
      { path: 'teacher/messages', element: <EditorRoute><TeacherMessagesPage /></EditorRoute> },
      { path: 'teacher/billing/:studentId', element: <EditorRoute><TeacherStudentBillingPage /></EditorRoute> },
      { path: 'teacher/billing', element: <EditorRoute><TeacherBillingPage /></EditorRoute> },
      { path: 'teacher/students/:studentId/questionnaire', element: <EditorRoute><TeacherStudentQuestionnairePage /></EditorRoute> },
      { path: 'teacher/students/:studentId/activity', element: <EditorRoute><TeacherStudentActivityPage /></EditorRoute> },
      { path: 'teacher/students/:studentId/topic-progress', element: <EditorRoute><StudentTopicProgressPage /></EditorRoute> },
      { path: 'teacher/students/:studentId', element: <EditorRoute><TeacherStudentWorkPage /></EditorRoute> },
      { path: 'teacher/lists/create', element: <EditorRoute><TeacherListCreatePage /></EditorRoute> },
      { path: 'teacher/lists', element: <EditorRoute><TeacherDashboardPage /></EditorRoute> },
      { path: 'teacher/lessons', element: <EditorRoute><TeacherDashboardPage /></EditorRoute> },
      { path: 'teacher', element: <EditorRoute><TeacherDashboardPage /></EditorRoute> },
      { path: 'teacher/tasks', element: <EditorRoute><TaskEditorPage /></EditorRoute> },
      { path: 'teacher/tasks/import', element: <EditorRoute><BulkImportPage /></EditorRoute> },
      { path: 'teacher/tags', element: <EditorRoute><TeacherTagsPage /></EditorRoute> },
      { path: 'teacher/tools', element: <EditorRoute><TeacherToolsPage /></EditorRoute> },
      { path: 'teacher/task-inbox', element: <EditorRoute><TeacherTaskInboxPage /></EditorRoute> },
      { path: 'teacher/ege-families', element: <EditorRoute><TaskBankFamiliesPage /></EditorRoute> },
      { path: 'teacher/ege-map', element: <EditorRoute><TeacherEgeMapPage /></EditorRoute> },
      { path: 'teacher/new-users', element: <AdminRoute><TeacherNewUsersPage /></AdminRoute> },
      { path: 'teacher/new-users/:userId/activity', element: <AdminRoute><TeacherNewUserActivityPage /></AdminRoute> },
      { path: 'collaborative', element: <CollaborativePage /> },
      { path: 'collaborative/:sessionId', element: <CollaborativePage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
    </ThemeProvider>
  );
}
